import React, { useEffect, useRef, useState } from 'react'
import { Table, Button, ButtonGroup } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'

import { useLanguage } from '../../contexts/LanguageContext'
import { POI_PATH } from '../../config/routes'
import {
  slugify,
  getPoiLabel,
  getPoiMarkerPath,
  getPoiColor,
  calculateZoom,
  getGpsxMax,
  getGpsxMin,
  getGpsyMax,
  getGpsyMin,
} from '../../utils/utils'
import config from '../../config/config'
import DisplayPois from '../PointsOfInterest/DisplayPois'

const RampageList = ({ pois }) => {
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const mapInstanceRef = useRef(null)
  const infoWindowRef = useRef(null)
  const { activeLanguage } = useLanguage()

  // Charge les POIs cachés depuis localStorage
  const getStoredHiddenPois = () => {
    const stored = localStorage.getItem('hiddenPois')
    return stored ? JSON.parse(stored) : []
  }

  // State local qui ajoute isDisplayed à chaque POI
  const [displayedPois, setDisplayedPois] = useState(() => {
    const hiddenPois = getStoredHiddenPois()
    return pois
      .filter((poi) => poi.status !== 'DRAFT')
      .map((poi) => ({
        ...poi,
        isDisplayed: !hiddenPois.includes(poi.id),
      }))
  })

  // Chargement de la carte Google Maps
  useEffect(() => {
    if (!pois || pois.length === 0) return

    const loadGoogleMapsScript = () => {
      if (document.getElementById('google-maps-script')) {
        loadMap()
        return
      }
      const script = document.createElement('script')
      script.id = 'google-maps-script'
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      script.async = true
      script.defer = true
      script.onload = loadMap
      document.head.appendChild(script)
    }

    const loadMap = () => {
      if (!window.google) return

      const gpsxMax = getGpsxMax(displayedPois, false) + 0.01
      const gpsxMin = getGpsxMin(displayedPois, false)
      const gpsyMax = getGpsyMax(displayedPois, false)
      const gpsyMin = getGpsyMin(displayedPois, false)

      const mapOptions = {
        zoom:
          gpsxMax && gpsyMax && gpsxMin && gpsyMin
            ? calculateZoom(gpsxMax, gpsxMin, gpsyMax, gpsyMin)
            : config.mapPoiZoom,
        center: {
          lat: (gpsxMax + gpsxMin) / 2,
          lng: (gpsyMax + gpsyMin) / 2,
        },
        mapTypeId: 'terrain',
        styles: [
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }],
          },
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [{ visibility: 'simplified' }, { color: '#7FBF7F' }],
          },
        ],
      }

      const map = new window.google.maps.Map(mapRef.current, mapOptions)
      mapInstanceRef.current = map

      const bounds = new window.google.maps.LatLngBounds()

      markersRef.current = [] // reset markers

      displayedPois.forEach((poi) => {
        addMarker(poi, map, bounds)
      })

      infoWindowRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: false,
        boxClass: 'infoWindowBox',
      })

      map.fitBounds(bounds)
    }

    const addMarker = (poi, map, bounds) => {
      const marker = new window.google.maps.Marker({
        position: { lat: poi.gpsx, lng: poi.gpsy },
        map: poi.isDisplayed ? map : null,
        title: poi.name,
        icon: getPoiMarkerPath(poi),
      })

      bounds.extend(marker.getPosition())
      markersRef.current.push({ poiId: poi.id, marker })

      marker.addListener('click', () => {
        const poiPath = `${POI_PATH.replace(
          ':slug',
          slugify(poi.labelFr),
        ).replace(':id', poi.id)}`
        infoWindowRef.current.setContent(`
          <div>
            <h4>${getPoiLabel(poi, activeLanguage)}</h4>
            <div style="text-align: center;">
              <button class='btn btn-primary' onclick="window.location.href='${poiPath}'">${t(
          'SHOW',
        )}</button>
              <button class='btn btn-secondary' onclick="document.getElementById('hide-poi-${
                poi.id
              }').click()">${t('HIDE')}</button>
            </div>
          </div>
        `)
        infoWindowRef.current.open(map, marker)
      })
    }

    if (!window.google) {
      loadGoogleMapsScript()
    } else {
      loadMap()
    }
  }, [displayedPois, activeLanguage, t])

  // Met à jour la visibilité des markers quand displayedPois change
  useEffect(() => {
    markersRef.current.forEach(({ poiId, marker }) => {
      const poi = displayedPois.find((p) => p.id === poiId)
      if (poi && marker) {
        marker.setMap(poi.isDisplayed ? mapInstanceRef.current : null)
      }
    })
  }, [displayedPois])

  // Met à jour le localStorage et le state quand on toggle l'affichage
  const handleToggleDisplay = (poiId, shouldDisplay) => {
    const updatedPois = displayedPois.map((poi) =>
      poi.id === poiId ? { ...poi, isDisplayed: shouldDisplay } : poi,
    )
    setDisplayedPois(updatedPois)
    const hiddenPois = updatedPois
      .filter((poi) => !poi.isDisplayed)
      .map((poi) => poi.id)
    localStorage.setItem('hiddenPois', JSON.stringify(hiddenPois))
  }

  return (
    <div className="mt-5">
      <Table bordered className="rdb-table">
        <thead>
          <tr>
            <th>{t('N°')}</th>
            <th>{t('CITY')}</th>
            <th>{t('LABEL')}</th>
            <th>{t('DISPLAYED')} ?</th>
          </tr>
        </thead>
        <tbody>
          {displayedPois.map((poi) => {
            const backgroundColor = getPoiColor(poi)
            return (
              <tr key={poi.id}>
                <td style={{ textAlign: 'center' }}>
                  <Link
                    style={{
                      '--background-color': backgroundColor,
                      '--background-color-rgb': backgroundColor,
                    }}
                    to={`${POI_PATH.replace(
                      ':slug',
                      slugify(poi.labelFr),
                    ).replace(':id', poi.id)}`}
                    className="poi-list-numero-link btn"
                  >
                    {poi.numero}
                  </Link>
                </td>
                <td>{poi.city.label}</td>
                <td>{getPoiLabel(poi, activeLanguage)}</td>
                <td style={{ textAlign: 'center' }}>
                  <ButtonGroup style={{ height: '32px' }}>
                    <Button
                      variant={poi.isDisplayed ? 'primary' : 'secondary'}
                      onClick={() => handleToggleDisplay(poi.id, true)}
                      className={`btn-sm poi-list-btn-sm-custom ${
                        poi.isDisplayed ? 'activated-noclic' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      id={`hide-poi-${poi.id}`}
                      variant={!poi.isDisplayed ? 'primary' : 'secondary'}
                      onClick={() => handleToggleDisplay(poi.id, false)}
                      className={`btn-sm poi-list-btn-sm-custom ${
                        !poi.isDisplayed ? 'activated-noclic' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faEyeSlash} />
                    </Button>
                  </ButtonGroup>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <div>
        <div ref={mapRef} style={{ height: '500px', width: '100%' }} />
      </div>

      <hr />
      <DisplayPois pois={pois} isCity={true} isCategory={false} />
    </div>
  )
}

export default RampageList
